import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import styles from './Modal.module.css';

// Centre-screen dialog. CLAUDE.md §5 reserves this for destructive, irreversible
// confirms and for anything the user explicitly asked to open as a dialog —
// everything else anchors to its trigger, and reversible actions just happen with
// a ten-second undo toast (CLAUDE.md §5a rule 8).

interface ModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: string;
  readonly footer?: ReactNode;
  /** `large`: a big read-only view the user asked to see mid-screen, such as the application form's preview (ADR 0076). */
  readonly size?: 'default' | 'large';
  readonly children: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  footer,
  size = 'default',
  children,
}: ModalProps): ReactNode {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  /** Where focus came from, so it can be given back on close (06-qol §2). */
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    openerRef.current = document.activeElement as HTMLElement | null;
    // Focus the dialog itself rather than the first control: reading the title
    // before the buttons is the point of a confirm.
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      // Keep Tab inside the dialog while it is open, but never trap it there
      // permanently — Esc always leaves (06-qol §2, no keyboard traps).
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      openerRef.current?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.scrim} onPointerDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {description !== undefined && (
              <p id={descriptionId} className={styles.description}>
                {description}
              </p>
            )}
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer !== undefined && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly title: string;
  readonly description: string;
  /** What will actually happen, when it is worth spelling out before the click. */
  readonly notice?: string;
  readonly confirmLabel: string;
  readonly isPending?: boolean;
}

/**
 * The destructive confirm. 06-qol §4: it names what will happen and requires a
 * deliberate action — so the confirm button is the only solid one in the footer.
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  notice,
  confirmLabel,
  isPending = false,
}: ConfirmDialogProps): ReactNode {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="dangerSolid" onClick={onConfirm} isPending={isPending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {notice !== undefined && <p className={styles.notice}>{notice}</p>}
    </Modal>
  );
}
